import { useEffect, useState, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { clamp } from '@/utils/statistics';
import { CHART_Z_DOMAIN, formatMetricValue, PRESET_Z_VALUES, type ScoreConversion } from '../utils';

const SVG_WIDTH = 360;
const SVG_HEIGHT = 174;
const CHART_LEFT = 18;
const CHART_RIGHT = 18;
const CHART_TOP = 18;
const CHART_BOTTOM = 42;
const Y_MAX = 1 / Math.sqrt(2 * Math.PI);
const ACTIVE_MARKER_COLOR = 'hsl(var(--warning))';

const MOBILE_BANDS = [
    {
        id: 'extreme-left',
        start: -3.4,
        end: -3,
        color: 'hsl(var(--viz-band-4))',
        title: 'Extremo inferior',
        rangeLabel: 'Abaixo de -3 DP',
        shareLabel: 'Aprox. 0,1% da população',
        description: 'Resultado muito raro, bem abaixo da média padronizada.',
    },
    {
        id: 'tail-left',
        start: -3,
        end: -2,
        color: 'hsl(var(--viz-band-3))',
        title: 'Cauda inferior',
        rangeLabel: 'Entre -3 e -2 DP',
        shareLabel: 'Aprox. 2,1% da população',
        description: 'Faixa rara abaixo da média.',
    },
    {
        id: 'shoulder-left',
        start: -2,
        end: -1,
        color: 'hsl(var(--viz-band-2))',
        title: 'Faixa inferior intermediária',
        rangeLabel: 'Entre -2 e -1 DP',
        shareLabel: 'Aprox. 13,6% da população',
        description: 'Zona abaixo da média, ainda relativamente frequente.',
    },
    {
        id: 'center',
        start: -1,
        end: 1,
        color: 'hsl(var(--viz-band-1))',
        title: 'Faixa central',
        rangeLabel: 'Entre -1 e +1 DP',
        shareLabel: 'Aprox. 68,2% da população',
        description: 'Concentração principal da curva normal.',
    },
    {
        id: 'shoulder-right',
        start: 1,
        end: 2,
        color: 'hsl(var(--viz-band-2))',
        title: 'Faixa superior intermediária',
        rangeLabel: 'Entre +1 e +2 DP',
        shareLabel: 'Aprox. 13,6% da população',
        description: 'Zona acima da média, ainda relativamente frequente.',
    },
    {
        id: 'tail-right',
        start: 2,
        end: 3,
        color: 'hsl(var(--viz-band-3))',
        title: 'Cauda superior',
        rangeLabel: 'Entre +2 e +3 DP',
        shareLabel: 'Aprox. 2,1% da população',
        description: 'Faixa rara acima da média.',
    },
    {
        id: 'extreme-right',
        start: 3,
        end: 3.4,
        color: 'hsl(var(--viz-band-4))',
        title: 'Extremo superior',
        rangeLabel: 'Acima de +3 DP',
        shareLabel: 'Aprox. 0,1% da população',
        description: 'Resultado muito raro, bem acima da média padronizada.',
    },
];

function normalDensity(z: number): number {
    return (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-(z * z) / 2);
}

function buildRange(start: number, end: number, step = 0.08): number[] {
    const values: number[] = [];

    for (let current = start; current < end; current += step) {
        values.push(Number(current.toFixed(4)));
    }

    values.push(end);

    return values;
}

function formatPreset(value: number): string {
    return value > 0 ? `+${value}` : String(value);
}

function getBandForZ(z: number) {
    return MOBILE_BANDS.find((band) => z >= band.start && z <= band.end) ?? MOBILE_BANDS[3];
}

export default function MobileNormalDistributionChart({
    conversion,
    onZChange,
}: {
    conversion: ScoreConversion | null;
    onZChange: (z: number) => void;
}) {
    const [selectedBandId, setSelectedBandId] = useState<string | null>(null);
    const xMin = CHART_Z_DOMAIN[0];
    const xMax = CHART_Z_DOMAIN[1];
    const plotWidth = SVG_WIDTH - CHART_LEFT - CHART_RIGHT;
    const plotHeight = SVG_HEIGHT - CHART_TOP - CHART_BOTTOM;
    const baselineY = CHART_TOP + plotHeight;
    const activeZ = clamp(conversion?.z ?? 0, xMin, xMax);

    useEffect(() => {
        setSelectedBandId(null);
    }, [activeZ]);

    const toX = (value: number) => CHART_LEFT + ((value - xMin) / (xMax - xMin)) * plotWidth;
    const toY = (value: number) => CHART_TOP + (1 - value / Y_MAX) * plotHeight;

    const curvePath = buildRange(xMin, xMax)
        .map((z, index) => {
            const command = index === 0 ? 'M' : 'L';
            return `${command}${toX(z).toFixed(2)},${toY(normalDensity(z)).toFixed(2)}`;
        })
        .join(' ');

    const bandPolygons = MOBILE_BANDS.map((band) => {
        const points = buildRange(band.start, band.end).map((z) => ({
            x: toX(z),
            y: toY(normalDensity(z)),
        }));

        const polygon = [
            `${toX(band.start).toFixed(2)},${baselineY.toFixed(2)}`,
            ...points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`),
            `${toX(band.end).toFixed(2)},${baselineY.toFixed(2)}`,
        ].join(' ');

        return {
            ...band,
            polygon,
        };
    });

    const activeBand = getBandForZ(activeZ);
    const selectedBand = selectedBandId
        ? MOBILE_BANDS.find((band) => band.id === selectedBandId)
        : null;
    const displayedBand = selectedBand ?? activeBand;
    const markerX = toX(activeZ);
    const markerY = toY(normalDensity(activeZ));

    const handleBandKeyDown = (event: KeyboardEvent<SVGPolygonElement>, bandId: string) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        setSelectedBandId(bandId);
    };

    return (
        <section className="rounded-4xl border border-border bg-background p-4 shadow-elevation-1">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-base font-semibold tracking-tight text-foreground">
                        Curva normal
                    </h2>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        Toque nas faixas ou use o controle para ajustar o Z.
                    </p>
                </div>
                <div className="rounded-2xl border border-border bg-surface px-3 py-2 text-right shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Z atual
                    </p>
                    <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                        {formatMetricValue('z', activeZ)}
                    </p>
                </div>
            </div>

            <svg
                viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                className="mt-4 h-auto w-full overflow-visible"
                role="img"
                aria-label="Curva normal simplificada para celular"
            >
                <line
                    x1={CHART_LEFT}
                    y1={baselineY}
                    x2={SVG_WIDTH - CHART_RIGHT}
                    y2={baselineY}
                    stroke="hsl(var(--muted-foreground))"
                    strokeOpacity="0.25"
                    strokeWidth="1"
                />

                {bandPolygons.map((band) => {
                    const isDisplayed = displayedBand.id === band.id;
                    const isActive = activeBand.id === band.id;

                    return (
                        <polygon
                            key={band.id}
                            points={band.polygon}
                            fill={band.color}
                            opacity={isDisplayed || isActive ? 1 : 0.78}
                            stroke={isDisplayed ? 'hsl(var(--warning))' : 'transparent'}
                            strokeWidth={isDisplayed ? 2 : 0}
                            role="button"
                            tabIndex={0}
                            className="cursor-pointer outline-none transition-opacity focus-visible:opacity-100"
                            onClick={() => setSelectedBandId(band.id)}
                            onKeyDown={(event) => handleBandKeyDown(event, band.id)}
                            aria-label={`${band.title}. ${band.rangeLabel}. ${band.shareLabel}.`}
                        />
                    );
                })}

                <path
                    d={curvePath}
                    fill="none"
                    stroke="hsl(var(--foreground))"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.75"
                />

                {[-2, -1, 0, 1, 2].map((tick) => {
                    const tickX = toX(tick);

                    return (
                        <g key={tick}>
                            <line
                                x1={tickX}
                                y1={baselineY}
                                x2={tickX}
                                y2={baselineY + 5}
                                stroke="hsl(var(--muted-foreground))"
                                strokeOpacity="0.5"
                            />
                            <text
                                x={tickX}
                                y={baselineY + 24}
                                fill="hsl(var(--muted-foreground))"
                                fontSize="11"
                                fontWeight={700}
                                textAnchor="middle"
                            >
                                {formatPreset(tick)}
                            </text>
                        </g>
                    );
                })}

                <line
                    x1={markerX}
                    y1={CHART_TOP + 6}
                    x2={markerX}
                    y2={baselineY}
                    stroke={ACTIVE_MARKER_COLOR}
                    strokeDasharray="5 5"
                    strokeWidth="2"
                />
                <circle cx={markerX} cy={markerY} r="7" fill={ACTIVE_MARKER_COLOR} />
                <circle cx={markerX} cy={markerY} r="3.5" fill="hsl(var(--surface))" />
            </svg>

            <div className="mt-4 space-y-3">
                <Slider
                    value={[Number(activeZ.toFixed(2))]}
                    min={xMin}
                    max={xMax}
                    step={0.05}
                    onValueChange={(value: number[]) => onZChange(value[0])}
                    aria-label="Ajustar escore Z"
                    className="py-2"
                />
                <div className="grid grid-cols-5 gap-2">
                    {PRESET_Z_VALUES.map((preset) => (
                        <Button
                            key={preset}
                            type="button"
                            variant={Math.abs(activeZ - preset) < 0.03 ? 'default' : 'surface'}
                            size="sm"
                            className={cn(
                                'h-9 rounded-2xl px-0 text-xs font-semibold tabular-nums',
                                Math.abs(activeZ - preset) < 0.03 && 'shadow-none',
                            )}
                            onClick={() => onZChange(preset)}
                        >
                            {formatPreset(preset)}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="mt-4 rounded-3xl border border-brand/15 bg-surface px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {selectedBand ? 'Faixa selecionada' : 'Faixa atual'}
                </p>
                <p className="mt-2 text-sm font-semibold text-foreground">{displayedBand.title}</p>
                <p className="mt-1 text-xs font-medium text-brand">
                    {displayedBand.rangeLabel} · {displayedBand.shareLabel}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    {displayedBand.description}
                </p>
            </div>
        </section>
    );
}
